import React, { useState, useEffect, useMemo } from "react";
import { Button } from "./button";
import { Card, CardContent } from "./card";

import InputField from "./InputField";
import SelectField from "./SelectField";
import TextAreaField from "./TextAreaField";

const EntityForm = ({ entity, type = "cliente", onClose, onSave }) => {
  const roles = useMemo(() => {
    return type === "proveedor"
      ? ["Proveedor", "Fabricante", "Representante"]
      : ["Cliente", "Distribuidor"];
  }, [type]);

  const [formData, setFormData] = useState({
    name: "",
    nit: "",
    address: "",
    phone: "",
    logo: "",
    email: "",
    role: roles[0],
  });

  useEffect(() => {
    if (entity && Object.keys(entity).length > 0) {
      setFormData({
        name: entity.name || "",
        nit: entity.nit || "",
        address: entity.address || "",
        phone: entity.phone || "",
        logo: entity.logo || "",
        email: entity.email || "",
        role: entity.role || roles[0],
      });
    } else {
      setFormData((prev) => ({ ...prev, role: roles[0] }));
    }
  }, [entity, roles]);

  // 🔑 HANDLER ÚNICO (compatible con TODOS los campos)
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await onSave(formData);
      onClose();
    } catch (err) {
      console.error(err);
    }
  };

  const title = entity
    ? `Editar ${type === "proveedor" ? "proveedor" : "cliente"}`
    : `Nuevo ${type === "proveedor" ? "proveedor" : "cliente"}`;

  const submitText = entity ? "Actualizar" : "Crear";

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4">
      {/* Modal */}
      <div className="w-full max-w-2xl max-h-[90vh] bg-white rounded-2xl shadow-lg overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b">
          <h2 className="text-lg font-semibold text-gray-800">{title}</h2>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700 text-xl"
          >
            ✕
          </button>
        </div>

        {/* Scroll interno */}
        <div className="flex-1 overflow-y-auto">
          <Card className="border-none shadow-none">
            <CardContent className="p-6">
              <form onSubmit={handleSubmit} className="space-y-4">
                {/* Nombre / NIT */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <InputField
                    label="Nombre *"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    placeholder={
                      type === "proveedor"
                        ? "Ej: Suministros VetPro S.A.S"
                        : "Ej: Clínica VetPlus"
                    }
                    required
                  />

                  <InputField
                    label="NIT *"
                    name="nit"
                    value={formData.nit}
                    onChange={handleChange}
                    placeholder="Ej: 901234567-8"
                    required
                  />
                </div>

                {/* Dirección */}
                <TextAreaField
                  label="Dirección *"
                  name="address"
                  value={formData.address}
                  onChange={handleChange}
                  placeholder="Ej: Calle 123 #45-67"
                  required
                />

                {/* Teléfono / Email */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <InputField
                    label="Teléfono *"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    placeholder="Ej: 3101234567"
                    required
                  />

                  <InputField
                    label="Correo *"
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    placeholder="Ej: contacto@vetplus.com"
                    required
                  />
                </div>

                {/* Logo */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 items-start">
                  <div>
                    <label className="block text-sm font-medium mb-1">
                      Logo
                    </label>
                    <input
                      type="file"
                      accept="image/*"
                      onChange={(e) => {
                        const file = e.target.files[0];
                        if (!file) return;
                        const reader = new FileReader();
                        reader.onloadend = () => {
                          setFormData((prev) => ({
                            ...prev,
                            logo: reader.result,
                          }));
                        };
                        reader.readAsDataURL(file);
                      }}
                      className="w-full border rounded p-2 bg-gray-50"
                    />
                  </div>

                  {formData.logo && (
                    <div>
                      <p className="text-sm font-medium mb-1">Logo actual</p>
                      <img
                        src={formData.logo}
                        alt="Logo"
                        className="h-32 w-full object-contain border rounded"
                      />
                    </div>
                  )}
                </div>

                {/* Rol */}
                <SelectField
                  label="Rol *"
                  name="role"
                  value={formData.role}
                  options={roles.map((r) => ({
                    _id: r,
                    name: r,
                  }))}
                  onChange={handleChange}
                />

                {/* Footer */}
                <div className="flex justify-end gap-2 pt-4">
                  <Button
                    type="button"
                    onClick={onClose}
                    className="bg-red-500 hover:bg-red-600 text-white"
                  >
                    Cancelar
                  </Button>
                  <Button
                    type="submit"
                    className={
                      entity
                        ? "bg-yellow-500 hover:bg-yellow-600 text-white"
                        : "bg-blue-600 hover:bg-blue-700 text-white"
                    }
                  >
                    {submitText}
                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default EntityForm;
